<template>
  <div class="revenue-share-overview">
    <template v-if="hasData">
      <!-- KPI Cards -->
      <section class="kpi-grid">
        <v-card
          v-for="(card, i) in kpiCards"
          :key="i"
          class="kpi-card"
          flat
        >
          <v-card-text>
            <div class="kpi-label">
              {{ card.label }}
            </div>
            <div class="kpi-value">
              {{ card.value }}
            </div>
            <div class="kpi-sub">
              {{ card.sub }}
            </div>
          </v-card-text>
        </v-card>
      </section>

      <!-- Share Distribution + Scope & Inputs -->
      <section class="mid-grid">
        <v-card
          class="panel-card"
          flat
        >
          <v-card-text>
            <div class="section-title">
              Revenue Share Distribution
            </div>

            <div
              v-for="row in shareRows"
              :key="row.key"
              class="share-row"
            >
              <div class="share-info">
                <span
                  class="share-dot"
                  :style="{ background: row.color }"
                />
                <span class="share-name">{{ row.label }}</span>
                <span class="share-percent">{{ row.percent }}%</span>
              </div>

              <div class="progress-bar-container">
                <div
                  class="progress-bar-fill"
                  :style="{ width: row.percent + '%', background: row.color }"
                />
              </div>

              <span class="share-amount">
                {{ row.amount | commaFormat }}
              </span>
            </div>
          </v-card-text>
        </v-card>

        <v-card
          class="panel-card"
          flat
        >
          <v-card-text>
            <div class="section-title">
              Scope & Inputs
            </div>

            <div class="info-list">
              <div
                v-for="row in scopeRows"
                :key="row.label"
                class="info-row"
              >
                <span class="info-label">{{ row.label }}</span>
                <span class="info-value">{{ row.value }}</span>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </section>

      <!-- Sessions Summary -->
      <section>
        <v-card
          class="panel-card"
          flat
        >
          <v-card-text>
            <div class="section-title">
              Sessions Summary
            </div>

            <div class="stat-grid">
              <div
                v-for="stat in sessionStats"
                :key="stat.label"
                class="stat-item"
              >
                <span class="stat-label">{{ stat.label }}</span>
                <span class="stat-value">{{ stat.value }}</span>
              </div>
            </div>

            <div
              v-if="excludedSessionsNote"
              class="excluded-note"
            >
              {{ excludedSessionsNote }}
            </div>
          </v-card-text>
        </v-card>
      </section>

      <!-- Charger-level Shares -->
      <section>
        <v-card
          class="panel-card"
          flat
        >
          <v-card-text>
            <div class="section-title">
              Charger-Level Shares
              <span class="count-badge">{{ chargerShareRows.length }}</span>
            </div>

            <v-data-table
              :headers="chargerHeaders"
              :items="chargerShareRows"
              :items-per-page="chargerPerPage"
              :page="chargerPage"
              item-key="charge_station_id"
              class="chargers-data-table"
              dense
              hide-default-footer
            >
              <template v-slot:item.partner="{ item }">
                {{ item.partner }}%
              </template>
              <template v-slot:item.dealer="{ item }">
                {{ item.dealer }}%
              </template>
              <template v-slot:item.pso="{ item }">
                {{ item.pso }}%
              </template>
              <template v-slot:item.shares_source="{ item }">
                <span
                  class="badge"
                  :class="`badge-${item.shares_source}`"
                >
                  {{ item.shares_source }}
                </span>
              </template>
            </v-data-table>

            <pagination
              v-if="chargerShareRows.length"
              :active-page="chargerPage"
              :per-page="chargerPerPage"
              :per-page-options="[10, 25, 50, 100]"
              :total-items="chargerShareRows.length"
              :last-page="chargerLastPage"
              @change="onChargerPaginationChange"
            />
          </v-card-text>
        </v-card>
      </section>
    </template>

    <div
      v-else-if="!isLoading"
      class="no-data"
    >
      {{ $t("no_data_available") }}
    </div>
  </div>
</template>

<script>
export default {
  components: {
    Pagination: () => import("@/layouts/section/Pagination.vue"),
  },
  props: {
    data: {
      type: Object,
      default: () => ({}),
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      chargerPage: 1,
      chargerPerPage: 10,
    };
  },
  computed: {
    hasData() {
      return !!(
        this.data &&
        !Array.isArray(this.data) &&
        Object.keys(this.data).length
      );
    },
    sap() {
      return this.data?.sap_format || {};
    },
    inputs() {
      return this.data?.inputs || {};
    },
    sessions() {
      return this.data?.sessions_summary || {};
    },
    scope() {
      return this.data?.scope || {};
    },
    window() {
      return this.data?.window || {};
    },
    chargerShares() {
      return this.data?.charger_shares || [];
    },
    chargerHeaders() {
      return [
        { text: "Charge Point", value: "charge_point_id" },
        { text: "Station ID", value: "charge_station_id" },
        { text: "Partner", value: "partner", align: "center" },
        { text: "Dealer", value: "dealer", align: "center" },
        { text: "PSO", value: "pso", align: "center" },
        { text: "Source", value: "shares_source" },
      ];
    },
    chargerShareRows() {
      return this.chargerShares.map((row) => ({
        charge_station_id: row.charge_station_id,
        charge_point_id: row.charge_point_id,
        partner: row.shares_percent?.partner ?? "—",
        dealer: row.shares_percent?.dealer ?? "—",
        pso: row.shares_percent?.pso ?? "—",
        shares_source: row.shares_source,
      }));
    },
    chargerLastPage() {
      return Math.max(
        1,
        Math.ceil(this.chargerShareRows.length / this.chargerPerPage)
      );
    },
    kpiCards() {
      return [
        {
          label: "Units Sold",
          value: `${this.$options.filters.commaFormat(
            this.sap.quantity_of_units_sold_kwh,
            false
          )} kWh`,
          sub: "energy dispensed in scope",
        },
        {
          label: "Revenue Collected",
          value: this.$options.filters.commaFormat(this.sap.revenue_collected),
          sub: "gross revenue billed to customers",
        },
        {
          label: "Electricity Cost",
          value: this.$options.filters.commaFormat(this.sap.electricity_cost),
          sub: "cost of units consumed",
        },
        {
          label: "Net Revenue",
          value: this.$options.filters.commaFormat(this.sap.net_revenue),
          sub: "revenue after electricity cost",
        },
      ];
    },
    shareRows() {
      const shares = this.inputs.shares_percent || {};
      return [
        {
          key: "partner",
          label: "Partner",
          percent: shares.partner ?? 0,
          amount: this.sap.partner_share,
          color: "#1b7a63",
        },
        {
          key: "dealer",
          label: "Dealer",
          percent: shares.dealer ?? 0,
          amount: this.sap.dealer_share,
          color: "#e08369",
        },
        {
          key: "pso",
          label: "PSO",
          percent: shares.pso ?? 0,
          amount: this.sap.pso_share,
          color: "#5b6fd8",
        },
      ];
    },
    scopeRows() {
      const scope = this.scope;
      const win = this.window;
      return [
        { label: "Period", value: this.data?.period || "—" },
        {
          label: "Window",
          value:
            win.start || win.end
              ? `${win.start || "—"} to ${win.end || "—"}`
              : "All time",
        },
        { label: "Product", value: (scope.product || "—").toUpperCase() },
        { label: "Status", value: scope.status || "—" },
        { label: "Revenue basis", value: this.inputs.revenue_basis || "—" },
        {
          label: "Min energy threshold",
          value: `${this.inputs.min_energy_kwh ?? "—"} kWh`,
        },
        {
          label: "Cost per kWh",
          value: this.$options.filters.commaFormat(
            this.inputs.cost_of_charging_units_per_kwh
          ),
        },
        {
          label: "Retail price per kWh",
          value: this.$options.filters.commaFormat(
            this.inputs.retail_price_of_charging_units_per_kwh
          ),
        },
        { label: "Loss", value: `${this.inputs.loss_percent ?? 0}%` },
        { label: "Shares basis", value: this.inputs.shares_source || "—" },
      ];
    },
    sessionStats() {
      const s = this.sessions;
      const fmt = this.$options.filters.commaFormat;
      return [
        { label: "Sessions", value: fmt(s.session_count, false) },
        {
          label: "Energy consumed",
          value: `${fmt(s.energy_consumed_kwh, false)} kWh`,
        },
        { label: "Gross revenue", value: fmt(s.gross_revenue) },
        { label: "Energy revenue", value: fmt(s.energy_revenue) },
        { label: "Tax collected", value: fmt(s.tax_collected) },
        { label: "Purchase cost", value: fmt(s.recorded_purchase_cost) },
        {
          label: "Peak purchase cost",
          value: fmt(s.recorded_peak_purchase_cost),
        },
        {
          label: "Off-peak purchase cost",
          value: fmt(s.recorded_off_peak_purchase_cost),
        },
        {
          label: "Avg retail price / kWh",
          value: fmt(s.avg_realised_retail_price_per_kwh),
        },
        {
          label: "Avg purchase price / kWh",
          value: fmt(s.avg_recorded_purchase_price_per_kwh),
        },
      ];
    },
    excludedSessionsNote() {
      const excluded = this.sessions?.excluded_low_energy_sessions;
      if (!excluded?.session_count) return "";
      const fmt = this.$options.filters.commaFormat;
      return `${fmt(
        excluded.session_count,
        false
      )} sessions excluded below the ${
        this.inputs.min_energy_kwh ?? 1
      } kWh energy threshold (${fmt(
        excluded.energy_consumed_kwh,
        false
      )} kWh, ${fmt(excluded.revenue)} revenue).`;
    },
  },
  watch: {
    chargerShareRows() {
      this.chargerPage = 1;
    },
  },
  methods: {
    onChargerPaginationChange(val) {
      this.chargerPage = val.page;
      this.chargerPerPage = val.itemsPerPage;
    },
  },
};
</script>

<style lang="sass" scoped>
.revenue-share-overview
  display: flex
  flex-direction: column
  gap: 1rem

.no-data
  text-align: center
  color: gray
  padding: 2rem 0

.panel-card, .kpi-card
  border-radius: 12px !important
  border: 1px solid #f0f2f5

.section-title
  display: flex
  align-items: center
  gap: 10px
  font-size: 16px
  font-weight: 700
  color: #111827
  margin-bottom: 1rem

.count-badge
  display: inline-flex
  align-items: center
  justify-content: center
  min-width: 24px
  height: 24px
  padding: 0 7px
  border-radius: 20px
  background: $theme-color
  color: white
  font-size: 12px
  font-weight: 600

/* KPI cards */
.kpi-grid
  display: grid
  grid-template-columns: repeat(4, 1fr)
  gap: 1rem

.kpi-label
  color: gray
  font-size: 0.875rem

.kpi-value
  font-size: 1.75rem
  font-weight: 700
  color: $theme-color
  margin-top: 0.25rem

.kpi-sub
  font-size: 0.75rem
  color: gray
  margin-top: 0.25rem

/* Mid grid: share distribution + scope */
.mid-grid
  display: grid
  grid-template-columns: 1.2fr 1fr
  gap: 1rem

.share-row
  display: grid
  grid-template-columns: 160px 1fr 140px
  align-items: center
  gap: 1rem
  padding: 0.6rem 0
  border-bottom: 1px solid #f3f4f6

  &:last-child
    border-bottom: none
    padding-bottom: 0

.share-info
  display: flex
  align-items: center
  gap: 8px

.share-dot
  width: 10px
  height: 10px
  border-radius: 50%
  flex: none

.share-name
  font-weight: 600
  font-size: 0.875rem

.share-percent
  color: gray
  font-size: 0.75rem

.progress-bar-container
  width: 100%
  height: 10px
  background-color: #f3f4f6
  border-radius: 6px
  overflow: hidden

.progress-bar-fill
  height: 100%
  border-radius: 6px

.share-amount
  text-align: right
  font-weight: 700
  font-size: 0.875rem

.info-list
  display: flex
  flex-direction: column

.info-row
  display: flex
  justify-content: space-between
  gap: 1rem
  padding: 0.5rem 0
  border-bottom: 1px solid #f3f4f6
  font-size: 0.875rem

  &:last-child
    border-bottom: none
    padding-bottom: 0

.info-label
  color: gray

.info-value
  font-weight: 600
  text-align: right

/* Sessions */
.stat-grid
  display: grid
  grid-template-columns: repeat(4, 1fr)
  gap: 1rem

.stat-item
  display: flex
  flex-direction: column
  gap: 0.25rem
  padding: 0.5rem 0

.stat-label
  color: gray
  font-size: 0.75rem

.stat-value
  font-weight: 700
  font-size: 1rem

.excluded-note
  margin-top: 1rem
  padding-top: 1rem
  border-top: 1px solid #f3f4f6
  color: gray
  font-size: 0.8rem

/* Charger-level shares */
.chargers-data-table
  :deep thead tr th
    font-size: 11px !important
    font-weight: 700 !important
    text-transform: uppercase

.badge
  display: inline-flex
  align-items: center
  justify-content: center
  width: fit-content
  padding: 2px 10px
  border-radius: 12px
  font-size: 0.7rem
  font-weight: 600
  text-transform: capitalize

.badge-default
  background-color: #eef1f6
  color: #4b5563

.badge-blended
  background-color: #fef3c7
  color: #92640a

.badge-custom, .badge-override
  background-color: #dcfce7
  color: #15803d

@media (max-width: 1200px)
  .kpi-grid, .stat-grid
    grid-template-columns: repeat(2, 1fr)

@media (max-width: 1024px)
  .mid-grid
    grid-template-columns: 1fr

@media (max-width: 600px)
  .kpi-grid, .stat-grid
    grid-template-columns: 1fr

  .share-row
    grid-template-columns: 1fr
    gap: 0.5rem

  .share-amount
    text-align: left
</style>
